import { Big } from 'big.js';
import { _isNil } from 'majora/lodash';

const CURRENCY_PRECISION = 2;

export class MoneyUtil {

	/**
	 * Rounds currency value to two decimal places using half-up rounding
	 * i.e. 1.235 will round to 1.24 and 1.232 will round to 1.23
	 */
	static roundCurrency(value: Big | number): Big {
		return _isNil(value) ? Big(0) : Big(value).round(CURRENCY_PRECISION, Big.roundHalfUp);
	}

	/**
	 * Gets sum total for array of currency values
	 */
	static sumCurrencyValues(values: (Big | number)[]): Big {
		return Big(values.reduce(
			(sum, item) => {
				const existingSum = this.roundCurrency(sum);
				const newItem = this.roundCurrency(item);
				return existingSum.plus(newItem);
			}, 0,
		));
	}

	/**
	 * Multiplies two currency values together
	 */
	static multiplyCurrencyValues(value1: Big | number, value2: Big | number): Big {
		if (_isNil(value1) || _isNil(value2)) {
			return Big(0);
		}

		return this.roundCurrency(Big(value1).times(Big(value2)));
	}

	/**
	 * Multiply a money amount by a percent.
	 * Percents are represented as decimal values between 0 and 100.
	 */
	static multiplyPercent(money: Big | number, percent: Big | number): Big {
		if (_isNil(money) || _isNil(percent)) {
			return Big(0);
		}

		const INTERMEDIATE_PRECISION = 6;
		const intermediate = Big(money).times(Big(percent)).round(INTERMEDIATE_PRECISION, Big.roundHalfUp);
		return this.roundCurrency(intermediate.div(100));
	}
}
